import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings, Zap, Target, BarChart } from "lucide-react";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/blog/ai-call-center-setup.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const HowToSetUpAICallCenter = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('blog.howToSetUpAICallCenter.metaTitle')}</title>
        <meta name="description" content={t('blog.howToSetUpAICallCenter.metaDescription')} />
        <meta name="keywords" content="how to set up AI call center, AI call center setup guide, implement AI customer service, AI contact center tutorial, automate call center with AI" />
        <link rel="canonical" href="https://call24x7.ai/blog/how-to-set-up-ai-call-center" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/how-to-set-up-ai-call-center" />
        <meta property="og:title" content={t('blog.howToSetUpAICallCenter.title')} />
        <meta property="og:description" content={t('blog.howToSetUpAICallCenter.metaDescription')} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": t('blog.howToSetUpAICallCenter.title'),
            "description": t('blog.howToSetUpAICallCenter.metaDescription'),
            "step": [
              { "@type": "HowToStep", "name": "Define Goals", "text": "Identify what call types to automate" },
              { "@type": "HowToStep", "name": "Choose Platform", "text": "Select AI call center software" },
              { "@type": "HowToStep", "name": "Configure AI", "text": "Train your AI with business knowledge" },
              { "@type": "HowToStep", "name": "Test & Launch", "text": "Run tests and go live" }
            ]
          })}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.howToSetUpAICallCenter.title')}
        category={t('blog.categories.implementationGuide')}
        date="May 15, 2025"
        readTime={`12 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI call center setup guide"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.howToSetUpAICallCenter.intro')}
        </p>

        <div className="grid md:grid-cols-4 gap-4 my-8">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Target className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.howToSetUpAICallCenter.step')} 1</div>
              <p className="text-sm text-muted-foreground">{t('blog.howToSetUpAICallCenter.step1Short')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Settings className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.howToSetUpAICallCenter.step')} 2</div>
              <p className="text-sm text-muted-foreground">{t('blog.howToSetUpAICallCenter.step2Short')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <Zap className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.howToSetUpAICallCenter.step')} 3</div>
              <p className="text-sm text-muted-foreground">{t('blog.howToSetUpAICallCenter.step3Short')}</p>
            </CardContent>
          </Card>
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-6 text-center">
              <BarChart className="w-8 h-8 text-primary mx-auto mb-2" />
              <div className="text-lg font-bold">{t('blog.howToSetUpAICallCenter.step')} 4</div>
              <p className="text-sm text-muted-foreground">{t('blog.howToSetUpAICallCenter.step4Short')}</p>
            </CardContent>
          </Card>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.howToSetUpAICallCenter.step1Title')}</h2>
        <p>{t('blog.howToSetUpAICallCenter.step1Intro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>{t('blog.howToSetUpAICallCenter.callTypes')}:</strong> {t('blog.howToSetUpAICallCenter.callTypesDesc')}</li>
          <li><strong>{t('blog.howToSetUpAICallCenter.automationLevel')}:</strong> {t('blog.howToSetUpAICallCenter.automationLevelDesc')}</li>
          <li><strong>{t('blog.howToSetUpAICallCenter.volume')}:</strong> {t('blog.howToSetUpAICallCenter.volumeDesc')}</li>
          <li><strong>{t('blog.howToSetUpAICallCenter.hours')}:</strong> {t('blog.howToSetUpAICallCenter.hoursDesc')}</li>
          <li><strong>{t('blog.howToSetUpAICallCenter.successMetrics')}:</strong> {t('blog.howToSetUpAICallCenter.successMetricsDesc')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.howToSetUpAICallCenter.step2Title')}</h2>
        <p>{t('blog.howToSetUpAICallCenter.step2Intro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li><strong>{t('blog.howToSetUpAICallCenter.quickSetup')}:</strong> {t('blog.howToSetUpAICallCenter.quickSetupDesc')}</li>
          <li><strong>{t('blog.howToSetUpAICallCenter.enterprise')}:</strong> {t('blog.howToSetUpAICallCenter.enterpriseDesc')}</li>
          <li><strong>{t('blog.howToSetUpAICallCenter.customBuilds')}:</strong> {t('blog.howToSetUpAICallCenter.customBuildsDesc')}</li>
        </ul>
        <p>{t('blog.howToSetUpAICallCenter.step2Outro')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.howToSetUpAICallCenter.step3Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">A. {t('blog.howToSetUpAICallCenter.knowledgeBaseTitle')}</h3>
        <p>{t('blog.howToSetUpAICallCenter.knowledgeBaseIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li>{t('blog.howToSetUpAICallCenter.knowledge1')}</li>
          <li>{t('blog.howToSetUpAICallCenter.knowledge2')}</li>
          <li>{t('blog.howToSetUpAICallCenter.knowledge3')}</li>
          <li>{t('blog.howToSetUpAICallCenter.knowledge4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">B. {t('blog.howToSetUpAICallCenter.callFlowTitle')}</h3>
        <p>{t('blog.howToSetUpAICallCenter.callFlowIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li>{t('blog.howToSetUpAICallCenter.callFlow1')}</li>
          <li>{t('blog.howToSetUpAICallCenter.callFlow2')}</li>
          <li>{t('blog.howToSetUpAICallCenter.callFlow3')}</li>
          <li>{t('blog.howToSetUpAICallCenter.callFlow4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">C. {t('blog.howToSetUpAICallCenter.integrationsTitle')}</h3>
        <p>{t('blog.howToSetUpAICallCenter.integrationsIntro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li>{t('blog.howToSetUpAICallCenter.integration1')}</li>
          <li>{t('blog.howToSetUpAICallCenter.integration2')}</li>
          <li>{t('blog.howToSetUpAICallCenter.integration3')}</li>
          <li>{t('blog.howToSetUpAICallCenter.integration4')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.howToSetUpAICallCenter.step4Title')}</h2>
        <p>{t('blog.howToSetUpAICallCenter.step4Intro')}</p>
        <ol className="list-decimal pl-6 my-4 space-y-2">
          <li>{t('blog.howToSetUpAICallCenter.test1')}</li>
          <li>{t('blog.howToSetUpAICallCenter.test2')}</li>
          <li>{t('blog.howToSetUpAICallCenter.test3')}</li>
          <li>{t('blog.howToSetUpAICallCenter.test4')}</li>
          <li>{t('blog.howToSetUpAICallCenter.test5')}</li>
        </ol>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.howToSetUpAICallCenter.step5Title')}</h2>
        <p>{t('blog.howToSetUpAICallCenter.step5Intro')}</p>
        <ul className="list-disc pl-6 my-4 space-y-2">
          <li>{t('blog.howToSetUpAICallCenter.monitor1')}</li>
          <li>{t('blog.howToSetUpAICallCenter.monitor2')}</li>
          <li>{t('blog.howToSetUpAICallCenter.monitor3')}</li>
          <li>{t('blog.howToSetUpAICallCenter.monitor4')}</li>
          <li>{t('blog.howToSetUpAICallCenter.monitor5')}</li>
        </ul>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.howToSetUpAICallCenter.step6Title')}</h2>
        <p>{t('blog.howToSetUpAICallCenter.step6Desc')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.howToSetUpAICallCenter.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.howToSetUpAICallCenter.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.howToSetUpAICallCenter.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default HowToSetUpAICallCenter;
